import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Alert, Button, Modal } from 'react-bootstrap'
import { Document, Page, pdfjs } from 'react-pdf'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowDown, faBan, faCut, faLink, faUndo } from '@fortawesome/free-solid-svg-icons'
import SegmentBadge from './SegmentBadge'
import CenteredSpinner from 'shared/centered-spinner'
import BoxOverlay from './BoxOverlay'
import PageBoxEditor from './PageBoxEditor'
import SplitStatusSidebar from './SplitStatusSidebar'
import { buildSegmentFiles } from './build-segment-files'
import { PREVIEW_SIZE, pdfPagePreviewer, pdfPageRenderer } from './rasterize'
import { SourceError, imagePagePreviewer, imagePageRenderer, isPdf, loadSource, releaseImageSource } from './image-source'
import {
  MAX_SEGMENT_PAGES,
  MAX_SOURCE_PAGES,
  boxesOn,
  everyPageSeparate,
  isBoxed,
  oneDocument,
  segmentLabels,
  segmentProblems,
  setBoxes,
  toSegments,
  toggleBreak,
  toggleSkip
} from './segments'

pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString()

const SEGMENT_COLORS = ['primary', 'success', 'warning', 'info', 'danger']
const PAGE_WIDTH = 560
// Letter proportions, held for a page until its own size is known so the column doesn't jump as pages load.
const DEFAULT_ASPECT = 11 / 8.5

const colorForIndex = (index) => SEGMENT_COLORS[index % SEGMENT_COLORS.length]

// A page of the source at a given width: a PDF page through react-pdf, or the photo itself.
const PageView = ({ imageSource, page, width, onPageSize }) => {
  if (imageSource) {
    const { url } = imageSource.pages[page - 1]
    return <img src={url} width={width} alt={`Page ${page}`} className="d-block" draggable={false} />
  }

  return (
    <Page
      pageNumber={page}
      width={width}
      renderTextLayer={false}
      renderAnnotationLayer={false}
      loading=""
      onLoadSuccess={(pdfPage) => {
        const { width: pageWidth, height } = pdfPage.getViewport({ scale: 1 })
        onPageSize(page, { width: pageWidth, height })
      }}
    />
  )
}

PageView.propTypes = {
  imageSource: PropTypes.object,
  page: PropTypes.number.isRequired,
  width: PropTypes.number.isRequired,
  onPageSize: PropTypes.func.isRequired
}

// Renders a page only while it is near the view, so a 200-page document holds a few renderings at a time. Its box
// keeps the page's height while unrendered, so nothing shifts.
const LazyThumbnail = ({ imageSource, pageNumber, pageSize, onPageSize, children }) => {
  const ref = useRef(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), {
      rootMargin: '1500px 0px'
    })
    observer.observe(ref.current)
    return () => observer.disconnect()
  }, [])

  const aspect = pageSize ? pageSize.height / pageSize.width : DEFAULT_ASPECT

  return (
    <div ref={ref} className="position-relative" style={{ width: PAGE_WIDTH, minHeight: PAGE_WIDTH * aspect }}>
      {visible && <PageView imageSource={imageSource} page={pageNumber} width={PAGE_WIDTH} onPageSize={onPageSize} />}
      {children}
    </div>
  )
}

LazyThumbnail.propTypes = {
  imageSource: PropTypes.object,
  pageNumber: PropTypes.number.isRequired,
  pageSize: PropTypes.shape({ width: PropTypes.number, height: PropTypes.number }),
  onPageSize: PropTypes.func.isRequired,
  children: PropTypes.node
}

const DocumentSplitterModal = ({ file, noun, position, total, restWholeCount = 0, onCancel, onConfirm, onKeepRestWhole }) => {
  const pdfRef = useRef(null)
  const [imageSource, setImageSource] = useState(null)
  const [split, setSplit] = useState(null)
  const [pageSizes, setPageSizes] = useState({})
  const [editingPage, setEditingPage] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [buildError, setBuildError] = useState(null)
  const [building, setBuilding] = useState(false)
  // Set once the modal is cancelled or gone, so a build still running then is thrown away.
  const closed = useRef(false)
  const pageRefs = useRef({})
  // A page picked in the sidebar, scrolled to once the page list is showing.
  const [scrollTarget, setScrollTarget] = useState(null)

  useEffect(() => {
    if (!scrollTarget || editingPage) return
    pageRefs.current[scrollTarget]?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    setScrollTarget(null)
  }, [scrollTarget, editingPage])

  const showPage = (page) => {
    setEditingPage(null)
    setScrollTarget(page)
  }

  // Skipping, cutting or finishing a page moves on to the next one, so a long packet is worked top to bottom.
  const advancePast = (page) => {
    if (page < split.pageCount) showPage(page + 1)
    else setEditingPage(null)
  }

  const skipPage = (page) => {
    const skipping = !split.skipped.includes(page)
    setSplit(toggleSkip(split, page))
    if (skipping) advancePast(page)
  }

  const cutAfter = (page) => {
    const cutting = !split.breaks.includes(page)
    setSplit(toggleBreak(split, page))
    if (cutting) advancePast(page)
  }

  const segments = useMemo(() => (split ? toSegments(split) : []), [split])
  const labels = useMemo(() => (split ? segmentLabels(split) : { pages: {}, boxes: {} }), [split])
  const problems = split ? segmentProblems(segments, noun) : []
  const Noun = noun.charAt(0).toUpperCase() + noun.slice(1)
  const boxLabel = (box) => `${Noun} ${labels.boxes[box.id] + 1}`
  const boxColor = (box) => colorForIndex(labels.boxes[box.id])

  const recordPageSize = (page, size) =>
    setPageSizes((prev) => (prev[page] ? prev : { ...prev, [page]: size }))

  useEffect(() => {
    if (isPdf(file)) return undefined

    let released = false
    let loaded = null
    loadSource(file)
      .then((source) => {
        if (released) {
          releaseImageSource(source)
          return
        }
        loaded = source
        setImageSource(source)
        setPageSizes(Object.fromEntries(source.pages.map(({ width, height }, i) => [i + 1, { width, height }])))
        setSplit(oneDocument(source.pageCount))
      })
      .catch((error) => {
        if (released) return
        setLoadError(
          error instanceof SourceError
            ? error.message
            : 'This image could not be opened. It may be damaged or in a format the browser can’t read.'
        )
      })
    return () => {
      released = true
      releaseImageSource(loaded)
    }
  }, [file])

  useEffect(() => () => {
    closed.current = true
  }, [])

  const cancel = () => {
    closed.current = true
    onCancel()
  }

  const handleLoad = (pdf) => {
    pdfRef.current = pdf
    if (pdf.numPages > MAX_SOURCE_PAGES) {
      setLoadError(`This file has ${pdf.numPages} pages; the maximum is ${MAX_SOURCE_PAGES}.`)
    } else {
      setSplit(oneDocument(pdf.numPages))
    }
  }

  const handleUpload = async () => {
    setBuilding(true)
    setBuildError(null)
    try {
      const renderPage = imageSource ? imagePageRenderer(imageSource) : pdfPageRenderer(pdfRef.current)
      const files = await buildSegmentFiles(file, segments, { renderPage, imageSource })
      if (!closed.current) onConfirm(files)
    } catch (error) {
      if (closed.current) return
      setBuildError(error.message || 'This file could not be split.')
      setBuilding(false)
    }
  }

  const skippedCount = split ? split.skipped.length : 0
  const summary = split
    ? `${segments.length} ${noun}${segments.length === 1 ? '' : 's'} from ${split.pageCount} page${
      split.pageCount === 1 ? '' : 's'
    }${skippedCount > 0 ? `, ${skippedCount} skipped` : ''}`
    : ''

  const renderPageCard = (page) => {
    const skipped = split.skipped.includes(page)
    const boxes = boxesOn(split, page)
    const boxed = boxes.length > 0
    const index = labels.pages[page]
    const color = skipped || boxed ? 'light' : colorForIndex(index)
    const size = pageSizes[page]

    return (
      <div
        ref={(node) => { pageRefs.current[page] = node }}
        className={`border border-3 border-${color} rounded mx-auto bg-white`}
        style={{ width: 'fit-content', scrollMarginTop: '1rem' }}
        data-testid={`splitter-page-${page}`}
      >
        <div className="d-flex align-items-center gap-2 px-2 py-1 border-bottom small">
          <strong>Page {page}</strong>
          {skipped && <span className="text-muted">Skipped</span>}
          {!skipped && boxed && <span className="text-muted">{boxes.length} {noun}{boxes.length === 1 ? '' : 's'}</span>}
          {!skipped && !boxed && <SegmentBadge color={color}>{Noun} {index + 1}</SegmentBadge>}
          <div className="ms-auto d-flex align-items-center gap-2">
            {!skipped && (
              <Button
                variant="light"
                className="text-primary"
                size="sm"
                onClick={() => setEditingPage(page)}
                data-testid={`splitter-mark-${page}`}
              >
                {boxed ? `Edit ${noun}s` : 'Several on this page'}
              </Button>
            )}
            <Button
              variant="light"
              className="text-primary border"
              size="sm"
              aria-pressed={skipped}
              onClick={() => skipPage(page)}
              data-testid={`splitter-skip-${page}`}
            >
              <FontAwesomeIcon icon={skipped ? faUndo : faBan} className="me-1" />
              {skipped ? 'Undo skip' : 'Skip'}
            </Button>
            {page < split.pageCount && (
              <Button
                variant="light"
                className="text-primary border"
                size="sm"
                onClick={() => showPage(page + 1)}
                data-testid={`splitter-next-${page}`}
              >
                <FontAwesomeIcon icon={faArrowDown} className="me-1" />
                Next
              </Button>
            )}
          </div>
        </div>
        <div style={{ opacity: skipped ? 0.4 : 1 }}>
          <LazyThumbnail imageSource={imageSource} pageNumber={page} pageSize={size} onPageSize={recordPageSize}>
            {boxed && size && !skipped && (
              <BoxOverlay boxes={boxes} scale={PAGE_WIDTH / size.width} labelFor={(box) => labels.boxes[box.id] + 1} colorFor={boxColor} />
            )}
          </LazyThumbnail>
        </div>
      </div>
    )
  }

  // The gap between two pages: a cut there starts a new document at the page below.
  const renderBreak = (page) => {
    const isBreak = split.breaks.includes(page)
    const besideBoxes = isBoxed(split, page) || isBoxed(split, page + 1)
    const active = isBreak && !besideBoxes

    const line = `flex-grow-1 m-0 ${active ? 'border-primary border-2 opacity-100' : ''}`

    // The button names what a click does; the state it would undo is the caption beside it.
    return (
      <div className="d-flex align-items-center gap-2 my-2 mx-auto" style={{ width: PAGE_WIDTH }}>
        <hr className={line} />
        {active && (
          <span className="small text-primary text-nowrap" data-testid={`splitter-break-state-${page}`}>
            New {noun} starts at page {page + 1}
          </span>
        )}
        <Button
          variant="light"
          className="text-primary border"
          size="sm"
          disabled={besideBoxes}
          aria-pressed={isBreak}
          onClick={() => cutAfter(page)}
          data-testid={`splitter-break-${page}`}
        >
          <FontAwesomeIcon icon={active ? faLink : faCut} className="me-1" />
          {active ? `Join pages ${page} and ${page + 1}` : `Split before page ${page + 1}`}
        </Button>
        <hr className={line} />
      </div>
    )
  }

  const pages = editingPage ? (
    <PageBoxEditor
      page={editingPage}
      pageSize={pageSizes[editingPage]}
      boxes={boxesOn(split, editingPage)}
      noun={noun}
      labelFor={boxLabel}
      colorFor={boxColor}
      onChange={(boxes) => setSplit((prev) => setBoxes(prev, editingPage, boxes))}
      renderPreview={imageSource ? imagePagePreviewer(imageSource, PREVIEW_SIZE) : pdfPagePreviewer(pdfRef.current)}
      renderPageView={(width) => (
        <PageView imageSource={imageSource} page={editingPage} width={width} onPageSize={recordPageSize} />
      )}
      onDone={() => advancePast(editingPage)}
    />
  ) : (
    <>
      <p className="small text-muted mx-auto" style={{ maxWidth: PAGE_WIDTH }}>
        {imageSource?.pageCount === 1 ? (
          <>Keep the photo as one {noun}, or use &quot;Several on this page&quot; to box each {noun} in it.</>
        ) : (
          <>
            Click <FontAwesomeIcon icon={faCut} /> between two pages to start a new {noun} there. Skip pages that
            aren&apos;t {noun}s, such as cover or summary pages. Use &quot;Several on this page&quot; to box each{' '}
            {noun} on a page that holds more than one.
          </>
        )}
      </p>
      <div className="d-flex flex-column pb-3">
        {split &&
          Array.from({ length: split.pageCount }, (_, i) => i + 1).map((page) => (
            <React.Fragment key={page}>
              {renderPageCard(page)}
              {page < split.pageCount && renderBreak(page)}
            </React.Fragment>
          ))}
      </div>
    </>
  )

  return (
    <Modal show onHide={cancel} fullscreen data-testid="splitter-modal">
      <Modal.Header closeButton>
        <Modal.Title as="h5">
          Split {file.name}
          {total > 1 && <span className="text-muted small ms-2">Document {position} of {total}</span>}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="d-flex p-0 overflow-hidden">
        <div className="flex-grow-1 overflow-auto p-3 bg-light-subtle">
          {loadError && <Alert variant="danger" className="mb-0">{loadError}</Alert>}
          {!loadError && !isPdf(file) && (split ? pages : <CenteredSpinner />)}
          {!loadError && isPdf(file) && (
            <Document
              file={file}
              onLoadSuccess={handleLoad}
              onLoadError={() => setLoadError('This PDF could not be opened. It may be damaged or password-protected.')}
              loading={<CenteredSpinner />}
            >
              {split && pages}
            </Document>
          )}
        </div>
        {!loadError && split && (
          <SplitStatusSidebar
            segments={segments}
            pageCount={split.pageCount}
            skipped={split.skipped}
            noun={noun}
            colorFor={colorForIndex}
            onSelect={showPage}
          />
        )}
      </Modal.Body>

      <Modal.Footer className="justify-content-between">
        <div className="d-flex align-items-center gap-2">
          {split && (
            <>
              {!editingPage && (
                <>
                  <Button variant="light" className="text-primary" size="sm" onClick={() => setSplit(oneDocument(split.pageCount))}>
                    One {noun}
                  </Button>
                  <Button
                    variant="light"
                    className="text-primary"
                    size="sm"
                    disabled={split.pageCount < 2}
                    onClick={() => setSplit(everyPageSeparate(split.pageCount))}
                  >
                    Every page separate
                  </Button>
                </>
              )}
              <span className="small text-muted" data-testid="splitter-summary">{summary}</span>
            </>
          )}
        </div>
        <div className="d-flex align-items-center gap-2">
          {(problems.length > 0 || buildError) && (
            <span className="small text-danger" data-testid="splitter-problems">
              {[...problems, buildError].filter(Boolean).join(' ')}
            </span>
          )}
          {restWholeCount > 0 && onKeepRestWhole && (
            <Button
              variant="light"
              className="text-primary border"
              title={`Uploads each of the other waiting files of up to ${MAX_SEGMENT_PAGES} pages as it is. This file and longer ones stay here to split.`}
              onClick={onKeepRestWhole}
              data-testid="splitter-keep-rest"
            >
              Upload the other {restWholeCount} file{restWholeCount === 1 ? '' : 's'} as one {noun} each
            </Button>
          )}
          <Button variant="light" className="text-primary" onClick={cancel}>
            {loadError ? 'Close' : 'Cancel'}
          </Button>
          {!loadError && (
            <Button
              variant="primary"
              disabled={!split || problems.length > 0 || building}
              onClick={handleUpload}
              data-testid="splitter-upload"
            >
              {building ? 'Preparing…' : `Upload ${segments.length} ${noun}${segments.length === 1 ? '' : 's'}`}
            </Button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  )
}

DocumentSplitterModal.propTypes = {
  file: PropTypes.object.isRequired,
  noun: PropTypes.string.isRequired,
  position: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  restWholeCount: PropTypes.number,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onKeepRestWhole: PropTypes.func
}

export default DocumentSplitterModal
